import React from 'react';
import { Form, useFormikContext } from 'formik';
import { Button } from '../../Button/Button';
import { Input } from '../../Input/Input';
import { ImageUpload } from '../../ImageUpload/ImageUpload';
import { Select } from '../../Select/Select';
import { AddMerchFormValues } from '../types';
import useAgenciesList from '../../../hooks/useAgenciesList';
import { useFilterOptions } from '../../../hooks/useFilterOptions';

interface MerchFormFieldsProps {
  onCancel: () => void;
  isEditMode?: boolean;
  existingImageUrl?: string;
}

export const MerchFormFields: React.FC<MerchFormFieldsProps> = ({
  onCancel,
  isEditMode = false,
  existingImageUrl,
}) => {
  const formik = useFormikContext<AddMerchFormValues>();
  const { agenciesList } = useAgenciesList();
  const { merchCategoryOptions } = useFilterOptions();

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    formik.handleBlur(e);
  };

  const handleSelectBlur = (fieldName: string) => {
    formik.setFieldTouched(fieldName, true, true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    Object.keys(formik.values).forEach((key) => {
      formik.setFieldTouched(key, true, true);
    });

    formik.submitForm();
  };

  return (
    <Form noValidate onSubmit={handleSubmit}>
      <ImageUpload
        fieldName="image"
        existingImageUrl={isEditMode ? existingImageUrl : undefined}
        isRequired={!isEditMode}
      />
      <Input
        id="name"
        name="name"
        label="Name"
        placeholder="Enter the name"
        value={formik.values.name}
        onChange={formik.handleChange}
        onBlur={handleInputBlur}
        error={formik.touched.name ? formik.errors.name : undefined}
        touched={formik.touched.name}
      />
      <Input
        id="description"
        name="description"
        label="Description"
        placeholder="Enter the description"
        value={formik.values.description}
        onChange={formik.handleChange}
        onBlur={handleInputBlur}
        error={formik.touched.description ? formik.errors.description : undefined}
        touched={formik.touched.description}
      />
      <Select
        id="category"
        name="category"
        label="Category"
        options={merchCategoryOptions}
        value={formik.values.category || ''}
        onChange={(value) => {
          formik.setFieldValue('category', value);
        }}
        onBlur={() => handleSelectBlur('category')}
        error={formik.touched.category ? (formik.errors.category as string) : undefined}
        placeholder="Choose Category"
      />
      <Select
        id="agency"
        name="agency"
        label="Agency"
        options={agenciesList.map((agency) => ({
          value: agency.id.toString(),
          label: agency.name,
        }))}
        value={formik.values.agency}
        onChange={(value) => {
          formik.setFieldValue('agency', value);
        }}
        onBlur={() => handleSelectBlur('agency')}
        error={formik.touched.agency ? (formik.errors.agency as string) : undefined}
        placeholder="Choose Agency"
      />
      <div className="form-actions">
        <Button variant="button" className="outline cancel" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="button" type="submit">
          {isEditMode ? 'Update Merch' : 'Add New Merch'}
        </Button>
      </div>
    </Form>
  );
};
